import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useWebSocket, CONNECTION_STATES } from "../contexts/WebSocketContext";
import { useApp } from "../contexts/AppContext";
import AgentChat from "./AgentChat";
import { PerformanceDashboard } from "./PerformanceDashboard";
import ActivityPanel from "./ActivityPanel";
import VisionDisplay from "./VisionDisplay";
import { OutreachDialog } from "./OutreachDialog";
import { CampaignCompleteDialog } from "./CampaignCompleteDialog";
import { motion, AnimatePresence } from "framer-motion";
import "./Dashboard.css";
import { useNavigate } from "react-router-dom";
import { useAgents } from "../hooks/useAgents";
import { authService } from "../services/auth";
import { useAuth } from "../hooks/useAuth";
import { ProfileMenu } from "./ProfileMenu";
import { getDatabase, remove, ref, orderByChild, limitToLast, query, get } from "firebase/database";
import FirebaseEvents from "../services/FirebaseEvents";
import PlaceholderUI from "./PlaceholderUI";
import InitializingUI from "./InitializingUI";
import { usePerformanceCard } from "./PerformanceCardContext";
import { useIsMobile } from "../hooks/useIsMobile";

export function Dashboard({ 
  campaignId, 
  campaignState: externalCampaignState,
  onCampaignStateChange,
  user,
  onLogout 
}) {
  // All hooks must be called first
  const { emit, on, off, connectionState, joinRoom } = useWebSocket();
  const isConnected = connectionState === CONNECTION_STATES.CONNECTED;
  const { addNotification } = useApp();
  const { resumeState, calendlyLink } = useAuth(); // Get resume state and calendlyLink from AuthProvider
  const navigate = useNavigate();
  const {
    agents,
    activeAgentsCount,
    setAgents,
    refresh: refreshAgentsList,
  } = useAgents();
  
  // Create ref to always get current agents without stale closures
  const agentsRef = useRef(agents);
  useEffect(() => {
    agentsRef.current = agents;
  }, [agents]);
  
  // Create ref for AgentChat container positioning
  const agentChatRef = useRef(null);
  
  // State declarations after hooks
  const [showOutreachDialog, setShowOutreachDialog] = useState(false);
  const [campaignUrl, setCampaignUrl] = useState("");
  const [enrichedProspects, setEnrichedProspects] = useState(null);
  const [jobToBeDone, setJobToBeDone] = useState(null);
  const [schedulerLink, setSchedulerLink] = useState(calendlyLink || "");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [campaignActive, setCampaignActive] = useState(false);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
 

  // Debug external campaign state
  console.log("[DASHBOARD_INIT] externalCampaignState:", externalCampaignState);
  
  // Force reset stuck states on load
  const finalCampaignState = useMemo(() => {
    if (externalCampaignState?.status === 'resuming' || externalCampaignState?.isInitializing) {
      console.log("[DASHBOARD_INIT] Resetting stuck campaign state");
      return {
        ...externalCampaignState,
        isInitializing: false,
        isStarting: false,
        status: 'stopped'
      };
    }
    return externalCampaignState || {
      isInitializing: false,
      isStarting: false,
      showChat: false,
      error: null,
      metrics: { emailsSent: 0, emailsOpened: 0, responses: 0, meetings: 0, responseRate: 0, conversionRate: 0 },
    };
  }, [externalCampaignState]);
  
  const [campaignState, setCampaignState] = useState(finalCampaignState);
  
  // Campaign mode state
  const [campaignMode, setCampaignMode] = useState('supervised');
  const [isTogglingMode, setIsTogglingMode] = useState(false);
  
  // Current prospect + Activity Watch / Vision Display state
  const [currentProspectInfo, setCurrentProspectInfo] = useState(null);
  const [showActivityPanel, setShowActivityPanel] = useState(false);
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showVisionDisplay, setShowVisionDisplay] = useState(false);
  const [canvasMode, setCanvasMode] = useState(null);
  const [canvasFilter, setCanvasFilter] = useState(null);
  
  // COO Chat Integration State
  // eslint-disable-next-line no-unused-vars
  const [_showFullChat, _setShowFullChat] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [_currentEmail, _setCurrentEmail] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [_feedbackMode, _setFeedbackMode] = useState(false);

    // Handler for opening Vision Display
  //const handleOpenVisionDisplay = useCallback((activity) => {
    //setSelectedActivity(activity);
    //setShowVisionDisplay(true);
  //}, []);

  // Handler for toggling Activity Panel
  const handleToggleActivityPanel = useCallback(() => {
    setShowActivityPanel(prev => !prev);
    if (showActivityPanel && isPanelExpanded) {
      setIsPanelExpanded(false);
    }
    // Clear canvas mode when closing
    if (showActivityPanel) {
      setCanvasMode(null);
      setCanvasFilter(null);
    }
  }, [showActivityPanel, isPanelExpanded]);
  
  // Handler for expanding/collapsing panel
  const handleExpandPanel = useCallback(() => {
    setIsPanelExpanded(prev => !prev);
  }, []);

  // Helper function to determine if human approval is needed
  const needsHumanApproval = useCallback(() => {
    // In supervised mode, surface the chat input whenever we're actively reviewing an email
    const needsApproval = campaignMode === 'supervised' && (_currentEmail !== null || _feedbackMode);
    console.log('[DEBUG] needsHumanApproval:', { campaignMode, _currentEmail, _feedbackMode, needsApproval });
    return needsApproval;
  }, [_currentEmail, _feedbackMode, campaignMode]);
  
  // Memoize the COO button visibility to prevent unnecessary re-renders
  const showCOOButton = useMemo(() => {
    // In supervised mode, always hide when approval needed, show when not needed
    if (campaignMode === 'supervised') {
      const shouldShow = !needsHumanApproval();
      console.log('[DEBUG] showCOOButton (supervised):', { needsHumanApproval: needsHumanApproval(), shouldShow });
      return shouldShow;
    }
    // In autonomous mode, show when campaign is active
    const shouldShow = campaignActive && campaignState?.status === 'active' && agents && agents.length > 0;
    console.log('[DEBUG] showCOOButton (autonomous):', { campaignActive, status: campaignState?.status, agentsCount: agents?.length, shouldShow });
    return shouldShow;
  }, [campaignMode, needsHumanApproval, campaignActive, campaignState?.status, agents]);

  // Handle COO (Chief of Staff) messages
  const handleCOOMessage = useCallback(async (message, files = []) => {
    try {
      const token = authService.getToken();
      const formData = new FormData();
      formData.append('message', message);
      formData.append('user_email', user?.email || '');
      formData.append('campaign_mode', campaignMode);
      
      // Append files if any
      files.forEach((fileData, index) => {
        if (fileData.file) {
          formData.append(`files[${index}]`, fileData.file);
        }
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/chat`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Store conversation in memory for context
      if (data.memory_id) {
        console.log('COO conversation stored with memory ID:', data.memory_id);
      }
      
      let responseContent = data.response || data.message || 'No response received';
      // Ensure we always return a string, not an object
      if (typeof responseContent !== 'string') {
        responseContent = JSON.stringify(responseContent);
      }
      return responseContent;
    } catch (error) {
      console.error('Error communicating with COO:', error);
      addNotification({
        type: 'error',
        message: 'Failed to communicate with Alice. Please try again.',
      });
      return 'I apologize, but I encountered an error. Please try again.';
    }
  }, [user, campaignMode, addNotification]);

  // Handle COO file uploads
  const handleCOOFileUpload = useCallback((files) => {
    console.log('Files uploaded to COO:', files);
    // Additional file handling logic can be added here
  }, []);

  // Update schedulerLink when calendlyLink changes from AuthProvider
  useEffect(() => {
    if (calendlyLink) {
      console.log('[Dashboard] Updating schedulerLink from calendlyLink:', calendlyLink);
      setSchedulerLink(calendlyLink);
    }
  }, [calendlyLink]);

  // Listen for canvas mode changes
  useEffect(() => {
    const handleOpenCanvas = (event) => {
      const { mode, filter } = event.detail;
      setShowActivityPanel(true);
      setCanvasMode(mode);
      setCanvasFilter(filter);
    };

    const handleExitCanvas = () => {
      setCanvasMode(null);
      setCanvasFilter(null);
    };

    window.addEventListener('openActivityCanvas', handleOpenCanvas);
    window.addEventListener('exitCanvasMode', handleExitCanvas);

    return () => {
      window.removeEventListener('openActivityCanvas', handleOpenCanvas);
      window.removeEventListener('exitCanvasMode', handleExitCanvas);
    };
  }, []);

  // Remove resumeState effect - let resume_started event handler manage this
  // This prevents race condition where effect fires before agents load

  // Event handler functions defined before use
  const handleInitError = useCallback(
    (error) => {
      console.error("Initialization error:", error);
      const errorMessage = error.message?.includes("NoneType")
        ? "Failed to initialize agent communication. Please try again in a few moments."
        : error.message || "Failed to initialize agents";

      setError(errorMessage);
      setIsLoading(false);
      setCampaignState((prev) => ({
        ...prev,
        // Don't change isInitializing here - only campaign_started should hide it
        isStarting: false,
        error: errorMessage,
        status: "stopped",
      }));
      addNotification({
        type: "error",
        message: errorMessage,
        duration: 5000,
      });
    },
    [addNotification]
  );
 
  const handleCampaignError = useCallback(
    (error) => {
      if (agentsRef.current?.length > 0 || isInitializingRef.current) return;
      const t = error?.sortTime ?? (typeof error?.timestamp === 'number' ? error.timestamp : (typeof error?.timestamp === 'string' ? Date.parse(error.timestamp) : undefined)); if (t && t < (window.sessionStartTime || Date.now())) return;

      console.debug("Campaign error:", error); // Changed to debug level
      setCampaignState((prev) => ({
        ...prev,
        error: error.message || "Campaign error occurred",
        isStarting: false,
      }));
      addNotification({
        type: "error",
        message: error.message || "Campaign error occurred",
      });
    },
    [addNotification]
  );

  // Toggle campaign mode function
  const handleToggleMode = useCallback(async () => {
    if (!authService.isAuthenticated() || isTogglingMode) return;
    
    setIsTogglingMode(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/campaign/toggle-mode`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authService.getToken()}`,
            Accept: "application/json",
          },
          credentials: "include",
          mode: "cors",
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setCampaignMode(result.mode);
      
      // Show notification
      addNotification({
        type: "info",
        message: `Campaign mode changed to ${result.mode}`,
        duration: 3000,
      });
      
      return result;
    } catch (error) {
      console.error("Error toggling campaign mode:", error);
      addNotification({
        type: "error",
        message: "Failed to toggle campaign mode",
      });
    } finally {
      setIsTogglingMode(false);
    }
  }, [isTogglingMode, addNotification]);

  // Use auth context instead of independent auth check
  useEffect(() => {
    // Auth is already checked by AuthProvider and ProtectedRoute
    // Just handle connection state notifications
    if (connectionState === CONNECTION_STATES.DISCONNECTED) {
      console.log("Socket disconnected, waiting for reconnection...");
      addNotification({
        type: "warning",
        message: "Attempting to reconnect to server...",
      });
    } else if (connectionState === CONNECTION_STATES.ERROR) {
      console.error("Socket connection error");
      addNotification({
        type: "error",
        message: "Failed to connect to server. Please refresh the page.",
      });
    }
  }, [connectionState, addNotification]);

  useEffect(() => {
    if (
      connectionState === CONNECTION_STATES.CONNECTED &&
      (!agents || agents.length === 0)
    ) {
      addNotification({
        type: "info",
        message: 'Click "Start New Campaign" to begin',
      });
    }
  }, [connectionState, agents, addNotification]);

  useEffect(() => {
    if (connectionState === CONNECTION_STATES.DISCONNECTED) {
      addNotification({
        type: "error",
        message: "Lost connection to server",
      });
    }
  }, [connectionState, addNotification]);

  useEffect(() => {
    if (connectionState === CONNECTION_STATES.CONNECTED && !agents.length) {
      refreshAgentsList();
    }
  }, [connectionState, agents.length, refreshAgentsList]);

  useEffect(() => {
    if (connectionState === CONNECTION_STATES.DISCONNECTED) {
      console.log("Socket disconnected, waiting for reconnection...");
    } else if (connectionState === CONNECTION_STATES.ERROR) {
      setError("WebSocket connection error");
      addNotification({
        type: "error",
        message: "Connection error. Please refresh the page.",
      });
    }
  }, [connectionState, addNotification]);

  useEffect(() => {
    if (connectionState !== CONNECTION_STATES.CONNECTED || !user?.email) return;

    console.log("Dashboard: Setting up socket listeners");
    
    // First join the room for metrics
    const encodedEmail = user.email.includes('@') ? 
      (() => {
        const [localPart, domain] = user.email.split('@');
        const encodedDomain = domain.replace(/\./g, '_');
        return `${localPart}@${encodedDomain}`;
      })() : user.email;
    
    console.log("[Dashboard] Joining room for metrics:", encodedEmail);
    
    // Fetch metrics immediately without waiting for room join
    // This ensures we get metrics on reload even if room join is slow
    try {
      const database = getDatabase();
      const firebaseEventsInstance = new FirebaseEvents(database, user.email);
      const basePath = firebaseEventsInstance.getPath(`events/metrics_update/${encodedEmail}`);
      console.log("[Dashboard] Fetching metrics from path:", basePath);
      const metricsRef = ref(database, basePath);
      const metricsQuery = query(metricsRef, orderByChild('sortTime'), limitToLast(1));
      
      get(metricsQuery).then((snapshot) => {
        const data = snapshot.val();
        console.log("[Dashboard] Fetched existing metrics from Firebase:", data);
        if (data) {
          const latestKey = Object.keys(data)[0];
          const latestMetrics = data[latestKey];
          if (latestMetrics?.content?.metrics) {
            console.log("[Dashboard] Applying fetched metrics:", latestMetrics.content.metrics);
            setCampaignState((prev) => {
              const updated = {
                ...prev,
                metrics: {
                  ...prev.metrics,
                  ...latestMetrics.content.metrics
                }
              };
              console.log("[Dashboard] Campaign state after metrics update:", updated);
              return updated;
            });
          }
        } else {
          console.log("[Dashboard] No metrics found at path:", basePath);
        }
      }).catch((error) => {
        console.error("[Dashboard] Error fetching metrics:", error);
      });
    } catch (error) {
      console.error("[Dashboard] Error setting up metrics fetch:", error);
    }
    
    // Also join room for real-time updates
    joinRoom(encodedEmail).then(() => {
      console.log("[Dashboard] Room joined successfully");
    });
    
    const handlers = {
      initialization_error: handleInitError,
      campaign_error: handleCampaignError,
      metrics_update: (data) => {
        console.log("[Dashboard] Metrics update received:", data);
        
        // Extract metrics from nested structure
        let metrics = null;
        
        // Handle Firebase structure: data.content.metrics
        if (data?.content?.metrics) {
          metrics = data.content.metrics;
        } else if (data?.metrics) {
          metrics = data.metrics;
        } else if (typeof data === 'object' && data !== null) {
          // Check if it's a Firebase collection with push IDs
          const entries = Object.entries(data).filter(([key]) => key.startsWith('-'));
          if (entries.length > 0) {
            const latestEntry = entries.sort((a, b) => b[0].localeCompare(a[0]))[0][1];
            metrics = latestEntry?.content?.metrics || latestEntry?.metrics;
          }
        }
        
        if (metrics) {
          console.log("[Dashboard] Extracted metrics:", metrics);
          setCampaignState((prev) => {
            const updated = {
              ...prev,
              metrics: {
                ...prev.metrics,
                ...metrics
              }
            };
            console.log("[Dashboard] Updated campaign state metrics:", updated.metrics);
            return updated;
          });
        } else {
          console.warn("[Dashboard] Could not extract metrics from:", data);
        }
      },
      campaign_started: (data) => {
        console.log("[Dashboard] Campaign started event received:", data);
        
        // Ignore stopped campaigns and failed events
        if (data?.status === 'stopped' || data?.success === false) {
          console.log("[Dashboard] Ignoring stopped/failed campaign_started event");
          return;
        }
        
        // Update campaign mode from backend if provided
        if (data?.mode) {
          console.log("[Dashboard] Setting campaign mode from backend:", data.mode);
          setCampaignMode(data.mode);
        }

        // Handle both direct data and Firebase event data
        const eventData = data.agents
          ? data
          : // If no direct agents, try to get from Firebase event
            Object.entries(data)
              .filter(
                ([key, value]) =>
                  key.startsWith("-") && value && typeof value === "object"
              )
              .sort((a, b) => b[0].localeCompare(a[0]))
              .map((entry) => entry[1])[0] || data;

        // Log the event data for debugging
        console.log("Processing campaign_started event:", {
          eventData,
          hasAgents: Boolean(eventData?.agents),
          campaignId: eventData?.campaign_id,
        });

        if (eventData && (Array.isArray(eventData.agents) || Array.isArray(data.agents))) {
          console.log(
            "Setting agents from campaign_started:",
            eventData.agents
          );

          // Batch state updates for better synchronization
          Promise.resolve().then(() => {
            setIsLoading(false);
            console.log(`[Dashboard] Campaign started with ${eventData.agents.length} agents`);
            console.log(`[Dashboard] Setting agents to:`, eventData.agents);
            
            // Set prospect data BEFORE setting agents
            if (eventData.prospect_data) {
              window.currentProspectEmail = eventData.prospect_data.email;
              window.currentProspectName = eventData.prospect_data.name;
              window.currentProspectImage = eventData.prospect_data.image_url;
              window.currentProspectTitle = eventData.prospect_data.title || eventData.prospect_data.position || '';
              window.currentProspectCompany = eventData.prospect_data.company || '';
              window.currentProspectLocation = eventData.prospect_data.location || '';
              window.currentProspectData = eventData.prospect_data;
              console.log("[PROSPECT FROM CAMPAIGN_STARTED] Set prospect data:", {
                name: eventData.prospect_data.name,
                email: eventData.prospect_data.email,
                hasImage: !!eventData.prospect_data.image_url
              });
              setCurrentProspectInfo(eventData.prospect_data);
            }
            
            setAgents(eventData.agents || data.agents);
            
            // Hide InitializingUI immediately when campaign starts
            hasReceivedFirstMessageRef.current = true;
            isInitializingRef.current = false;
            
            setCampaignState((prev) => ({
              ...prev,
              isInitializing: false,  // Always hide InitializingUI when campaign starts with agents
              showChat: true,  // Show chat since we know agents exist at this point
              isStarting: false,
              campaign_id: eventData.campaign_id,
              status: "active",  // Set to active immediately
              startTime:
                typeof campaignState.startTime === "number"
                  ? new Date(campaignState.startTime).toISOString()
                  : new Date().toISOString(),
              error: null,
            }));
            setCampaignActive(true);

            setShowOutreachDialog(false);

            // Log state updates
            console.log("Updated campaign state:", {
              agents: eventData.agents,
              campaignId: eventData.campaign_id,
              status: "active",
            });

            addNotification({
              type: "success",
              message: "Campaign is now active",
            });
          });
        } else if (eventData && eventData.error) {
          console.error("Campaign start failed:", eventData);
          handleCampaignError(eventData);
        } else {
          console.log(
            "Ignoring campaign_started event with invalid data:",
            eventData
          );
        }
      },
    };

    Object.entries(handlers).forEach(([event, handler]) => {
      on(event, handler);
    });

    return () => {
      Object.entries(handlers).forEach(([event, handler]) => {
        off(event, handler);
      });
    };
  }, [
    on,
    off,
    setAgents,
    handleInitError,
    handleCampaignError,
    addNotification,
    connectionState,
    user,
    joinRoom,
  ]);

  useEffect(() => {
    if (agents && Array.isArray(agents)) {
      console.log("Agents updated in Dashboard:", agents);
      console.log("Updating active agents count:", activeAgentsCount);

      // Set campaign as active if:
      // 1. We have agents OR
      // 2. We have an active campaign state OR
      // 3. We have a campaign ID
      const shouldBeActive =
        agents.length > 0 || campaignState.status === "active";
      campaignState.campaign_id;

      if (shouldBeActive) {
        console.log("Activating campaign based on state:", {
          agentsPresent: agents.length > 0,
          campaignStatus: campaignState.status,
          campaignId: campaignState.campaign_id,
        });

        // Use Promise.resolve to batch state updates
        Promise.resolve().then(() => {
          setCampaignActive(true);
        });
      }
    }
  }, [
    agents,
    campaignState,
    campaignState.startTime,
  ]);

  useEffect(() => {
    // Check token every 4 minutes
    const tokenCheckInterval = setInterval(async () => {
      try {
        const isValid = await authService.checkTokenExpiration();
        if (!isValid) {
          // Token refresh failed, redirect to login
          navigate("/login");
        }
      } catch (error) {
        console.error("Token check failed:", error);
        navigate("/login");
      }
    }, 240000); // 4 minutes

    return () => clearInterval(tokenCheckInterval);
  }, [navigate]);

  useEffect(() => {
    console.log("Dashboard state changed:", {
      campaignActive,
      agentsCount: agents?.length,
      showOutreachDialog,
      isLoading,
      campaignState,
    });
  }, [
    campaignActive,
    agents,
    showOutreachDialog,
    isLoading,
    campaignState,
  ]);

  useEffect(() => {
    if (isConnected) {
      console.log("Dashboard connected, requesting initial data");
      emit("request_dashboard_data");
    }
  }, [emit, isConnected]);

  useEffect(() => {
    // Update campaign state when external campaign changes
    // Only reset if we don't have a valid external campaign state
    if (!externalCampaignState) {
      setCampaignState({
        isInitializing: false,  // Keep false for initial state since no campaign is running
        isStarting: false,
        showChat: false,  // Reset showChat when no campaign is running
        error: null,
        metrics: { emailsSent: 0, emailsOpened: 0, responses: 0, meetings: 0, responseRate: 0, conversionRate: 0 },
        startTime: new Date().toISOString(),
        campaign_id: campaignId,
        status: "stopped",
      });
      setCampaignActive(false);
      setAgents([]);
    } else {
      // Use the external campaign state but preserve existing metrics
      console.log("[DASHBOARD] external campaign state", externalCampaignState)
      setCampaignState(prev => ({
        ...externalCampaignState,
        metrics: {
          ...prev.metrics,  // Keep existing metrics
          ...externalCampaignState.metrics  // Merge with any new metrics from external
        }
      }));
      setCampaignActive(externalCampaignState.status === "active");
      // Only reset agents if we don't have any in the external state
      if (!externalCampaignState.agents) {
        // Do nothing, keep existing agents
      }
    }
  }, [campaignId, externalCampaignState]);

  // Firebase is now initialized in WebSocketContext via AppMessagingService
  // No need for separate window.firebaseEvents instance

  // Set up resume event listeners immediately on mount (without waiting for connection)
  // This ensures we catch resume events even if they fire early
  const hasReceivedFirstMessageRef = useRef(false);
  const isInitializingRef = useRef(false);
  
  useEffect(() => {
    // Register listeners immediately, don't wait for connection
    // Use Firebase events for resume events since backend emits to Firebase
    if (!user?.email) return; // Ensure FirebaseEvents is initialized with user email before registering listeners
    
    // Listen for resume_started event to immediately show InitializingUI
    const handleResumeStarted = (data) => {
      console.log("[RESUME] resume_started event received:", data);
      console.log("[RESUME] resume_started - is_already_running:", data?.is_already_running);
      console.log("[RESUME] Current agents count:", agentsRef.current.length);
      
      // Don't process if campaign is already stopped or completed (unless we have is_already_running flag)
      if (!data?.is_already_running && (campaignState.status === 'stopped' || campaignState.status === 'completed')) {
        console.log("[RESUME] Campaign already stopped/completed, ignoring resume_started");
        return;
      }
      
      // Update campaign mode from backend if provided
      if (data?.mode) {
        console.log("[RESUME] Setting campaign mode from backend:", data.mode);
        setCampaignMode(data.mode);
      }
      
      // Sync resumeState with data.is_resuming for consistency
      if (data?.is_resuming !== undefined && resumeState?.isResuming !== data.is_resuming) {
        console.log("[RESUME] Syncing resumeState with data.is_resuming:", data.is_resuming);
      }
      
      // If campaign already active or first message received, don't change UI
      if (agentsRef.current.length > 0 || hasReceivedFirstMessageRef.current || campaignState.showChat || campaignState.status === 'active') { console.log("[RESUME] Agents already loaded or campaign active, skipping UI changes"); return; }
      
      // Check if campaign is actually running based on backend data
      // If is_already_running is false and we're not resuming, don't show InitializingUI
      if (data?.is_already_running === false) {
        console.log("[RESUME] Campaign not running (is_already_running: false), skipping InitializingUI");
        setCampaignActive(false);  // Ensure campaign is marked as inactive
        return;
      }
      
      // Ignore if AuthProvider says we're NOT resuming OR if campaign is already running
      if (resumeState?.isResuming === false || data?.is_already_running === true) {
        console.log("[RESUME] Ignoring resume_started - not resuming or already running");
        return;
      }
      
      // Show InitializingUI until campaign_started brings agents
      console.log("[RESUME] No agents yet, showing InitializingUI");
      isInitializingRef.current = true;
      hasReceivedFirstMessageRef.current = false;
      setCampaignState((prev) => {
        // Get fresh agents count at time of state update
        const hasAgents = agentsRef.current.length > 0;
        if (hasAgents) {
          console.log("[RESUME] Agents appeared during state update, skipping UI change");
          return prev;
        }
        return {
          ...prev,
          isInitializing: true,  // Only show since we verified no agents
          showChat: false,  // Keep chat hidden until campaign_started
          isStarting: false,
          campaign_id: data.campaign_id,
          status: "resuming",
          error: null,
          is_already_running: data.is_already_running,  // Track if already running
        };
      });
    };
    
    // Listen for agents_initialized event (for BOTH resume AND normal flow)
    const handleAgentsInitialized = async (data) => {
      console.log("[RESUME] agents_initialized event received:", data);
      console.log("[RESUME] agents_initialized - is_already_running:", data?.is_already_running);
      console.log("[RESUME] agents_initialized - message:", data?.message);
      console.log("[RESUME] agents_initialized - content:", data?.content);
      
      // Check if this is a normal campaign start (not resume) - check this BEFORE status check
      const isNormalStart = (data?.success !== false) && 
                           (data?.message === "Sales Agent Initialized successfully" ||
                            (data?.is_already_running !== true && !data?.message?.includes("Resuming")));
      
      if (isNormalStart) {
        // Handle normal campaign start flow
        console.log("[START] Processing normal campaign start agents_initialized");
        // Use the helper function from startCampaign
        if (window.processNormalStartAgentsInitialized) {
          return window.processNormalStartAgentsInitialized(data);
        }
        return;
      }
      
      // Don't process if campaign is already stopped or completed (only for resume flows)
      if (campaignState.status === 'stopped' || campaignState.status === 'completed') {
        console.log("[RESUME] Campaign already stopped/completed, ignoring agents_initialized");
        return;
      }
      
      // If campaign already active or first message received, don't change UI
      if (agentsRef.current.length > 0 || hasReceivedFirstMessageRef.current || campaignState.showChat || campaignState.status === 'active') { console.log("[RESUME] agents_initialized - Agents already loaded or campaign active, skipping UI changes"); return; }
      
      // Check if campaign is actually running based on backend data
      // If is_already_running is false, don't show InitializingUI
      if (data?.is_already_running === false) {
        console.log("[RESUME] agents_initialized - Campaign not running (is_already_running: false), skipping InitializingUI");
        setCampaignActive(false);  // Ensure campaign is marked as inactive
        return;
      }
      
      // Keep showing InitializingUI until campaign_started brings agents
      console.log("[RESUME] Keeping InitializingUI visible until campaign_started");
      isInitializingRef.current = true;  // Keep InitializingUI visible
      hasReceivedFirstMessageRef.current = false;  // Not ready yet
      setCampaignState((prev) => ({
        ...prev,
        isInitializing: agentsRef.current.length === 0,  // Only show if truly no agents
        showChat: false,  // Keep chat hidden until campaign_started
        isStarting: false,
        campaign_id: data.campaign_id || prev.campaign_id,
        scheduler_link: data.scheduler_link || prev.scheduler_link,
        website: data.website || prev.website,
        mode: data.mode || prev.mode,
        status: "resuming",  // Still resuming
        error: null,
        is_already_running: data.is_already_running,
      }));
      
      // Store scheduler_link for prefilling but don't set agents yet
      if (data?.scheduler_link) {
        setSchedulerLink(data.scheduler_link);
      }
      
      // Don't process the rest for already running campaigns
      if (data.is_already_running === true) {
        window.isAlreadyRunning = true;  // Set global flag for FirebaseEvents to use
        // Extract current prospect from content (only if recent)
        const messageTime = data?.timestamp ? new Date(data.timestamp).getTime() : Date.now();
        const isRecent = (Date.now() - messageTime) < 30000; // 30 seconds
        
        if (isRecent && data.content && data.content.includes('Campaign active for ')) {
          const match = data.content.match(/Campaign active for ([\w.-]+@[\w.-]+)/);
          if (match) {
            window.currentProspectEmail = match[1];
            setCurrentProspectInfo((prev) => ({
              ...(prev || {}),
              email: match[1],
            }));
            console.log("[RESUME] Current prospect email:", window.currentProspectEmail);
          }
        }
        // Only change UI if no agents loaded yet
        if (agentsRef.current.length === 0) {
          setCampaignState(prev => ({ 
            ...prev, 
            isInitializing: true,  // OK to show since no agents
            showChat: false 
          }));
        }
        return;
      }
      
      // But also check if is_already_running is explicitly false (campaign not running)
      if (data.is_already_running === false) {
        console.log("[RESUME] agents_initialized - Campaign not running (is_already_running: false), not processing");
        setCampaignActive(false);  // Ensure campaign is marked as inactive
        return;
      }
      
      // Normal resume flow
      // Backend does not set is_resume; infer resume via prior resume_started or message/content
      const inferredResume =
        isInitializingRef.current ||
        data?.message === "Resuming campaign..." ||
        (typeof data?.content === "string" && data.content.startsWith("Resuming campaign"));

      if (inferredResume) {
        // If agents already exist, skip UI changes
        if (agentsRef.current.length > 0) {
          console.log("[RESUME] Inferred resume - Agents already loaded, skipping UI changes");
          return;
        }
        console.log("[RESUME] Inferred resume flow, showing InitializingUI");
        isInitializingRef.current = true;
        hasReceivedFirstMessageRef.current = false;
        setCampaignState((prev) => ({
          ...prev,
          isInitializing: agentsRef.current.length === 0,
          showChat: false,  // Keep chat hidden until campaign_started
          isStarting: false,
          campaign_id: data.campaign_id || prev.campaign_id,
          scheduler_link: data.scheduler_link || prev.scheduler_link,
          website: data.website || prev.website,
          mode: data.mode || prev.mode,
          status: "resuming",
          error: null,
          is_already_running: data.is_already_running,
        }));
        // Set agents if provided
        if (data?.agents) {
          setAgents(data.agents);
        }
        // Store scheduler_link for prefilling
        if (data?.scheduler_link) {
          setSchedulerLink(data.scheduler_link);
        }
      }
    };
    
    // Listen for first agent message to hide InitializingUI
    const handleGroupChatMessage = (data) => {
      console.log("[RESUME] group_chat_message received:", data);
      
      // Don't process if campaign is already stopped or completed (check agents array for active state)
      if (!resumeState?.isResuming && agentsRef.current.length === 0 && (campaignState.status === 'stopped' || campaignState.status === 'completed')) {
        console.log("[RESUME] Campaign already stopped/completed, ignoring group_chat_message");
        return;
      }
      
      // Calculate message timing (needed for multiple checks below)
      const messageTime = data?.sortTime || (data?.timestamp ? new Date(data.timestamp).getTime() : Date.now());
      const isRecent = (Date.now() - messageTime) < 30000; // 30 seconds
      
      // Don't process UI state changes if we're not resuming OR the campaign is not running
      const shouldSkipUIUpdates = (resumeState?.isResuming === false || data?.is_already_running === false);
      
      if (!shouldSkipUIUpdates) {
        // If we receive messages during resume, hide the InitializingUI
        if (resumeState?.isResuming && campaignState.isInitializing) {
          setCampaignState(prev => ({ ...prev, isInitializing: false }));
          setCampaignActive(true);
        }
        
        // If we get a fresh message and agents exist, show chat
        if (isRecent && agentsRef.current.length > 0) {
          setCampaignState(prev => ({ ...prev, isInitializing: false, showChat: true, status: 'active' }));
          setCampaignActive(true);
        } else if (isRecent) {
          // Just mark as active but don't change UI if no agents
          setCampaignActive(true);
        }
      }
      
      // Always process prospect data regardless of resume state (for normal campaigns)
      if (data?.prospect_data) {
        window.currentProspectEmail = data.prospect_data.email;
        window.currentProspectName = data.prospect_data.name;
        window.currentProspectImage = data.prospect_data.image_url;
        console.log("[PROSPECT] Set prospect data:", {
          name: data.prospect_data.name,
          email: data.prospect_data.email,
          hasImage: !!data.prospect_data.image_url
        });
        
        // Update agents array to trigger AgentAvatar re-render for DigitalTwin
        setAgents(prevAgents => {
          return prevAgents.map(agent => {
            if (agent.name?.toLowerCase().includes('digitaltwin')) {
              return {
                ...agent,
                _prospectName: data.prospect_data.name,
                _prospectEmail: data.prospect_data.email,
                _prospectImage: data.prospect_data.image_url ?? '',
                _prospectUpdate: Date.now() // Force re-render
              };
            }
            return agent;
          });
        });
      }
      
      if (isRecent && data?.content && typeof data.content === 'string') {
        // Check for "Campaign active for xxx@xxx.com" (resume case)
        if (data.content.includes('Campaign active for ')) {
          const prospectMatch = data.content.match(/Campaign active for ([\w.-]+@[\w.-]+)/);
          if (prospectMatch) {
            window.currentProspectEmail = prospectMatch[1];
            setCurrentProspectInfo((prev) => ({
              ...(prev || {}),
              email: prospectMatch[1],
            }));
            console.log("[RESUME] Current prospect email from 'Campaign active':", window.currentProspectEmail);
          }
        }
        // Check for "I am reaching out to..." (new outreach case)
        else if (data.content.includes('I am reaching out to')) {
          // Extract email from the content - it should be in the prospect details
          const emailMatch = data.content.match(/\b([\w.-]+@[\w.-]+)\b/);
          if (emailMatch) {
            window.currentProspectEmail = emailMatch[1];
            setCurrentProspectInfo((prev) => ({
              ...(prev || {}),
              email: emailMatch[1],
            }));
            console.log("[OUTREACH] Current prospect email from 'I am reaching out':", window.currentProspectEmail);
          }
        }
      }
      // Don't show chat here - only campaign_started should show it
      // Check if campaign was already active when page loaded (isAlreadyRunning flag)
      if (!hasReceivedFirstMessageRef.current && isInitializingRef.current && 
          (campaignState.is_already_running === true || data?.is_already_running === true)) {
        console.log("[RESUME] First agent message received on reload");
        hasReceivedFirstMessageRef.current = true;
        isInitializingRef.current = false;
        setCampaignState((prev) => ({
          ...prev,
          isInitializing: false,
          showChat: true,
          campaign_id: data?.campaign_id || prev.campaign_id,
          status: "active",
        }));
      }
    };
    
    // Register all resume-related listeners using context
    console.log("[RESUME] Registering resume event listeners");
    on("resume_started", handleResumeStarted);
    on("agents_initialized", handleAgentsInitialized);
    on("group_chat_message", handleGroupChatMessage);
    on("message", handleGroupChatMessage);  // Also listen to normalized messages
    console.log("[RESUME] Resume event listeners registered");
    
    return () => {
      console.log("[RESUME] Cleaning up resume event listeners");
      off("resume_started", handleResumeStarted);
      off("agents_initialized", handleAgentsInitialized);
      off("group_chat_message", handleGroupChatMessage);
      off("message", handleGroupChatMessage);
    };
  }, [on, off, setAgents, setCampaignState, user]); // Don't include agents to avoid stale closures

  // Function to handle starting outreach
  const handleStartOutreach = async (data) => {
    try {
      if (!isConnected) { 
        throw new Error(
          "Server is not running. Please start the local server and try again."
        );
      }

      // Clear old Firebase events first
      const firebaseDb = getDatabase();
      const events = [
        "agents_initialized",
        "campaign_started",
        "start_campaign",
      ];
      const firebaseEventsInstance = new FirebaseEvents(firebaseDb, user.email);
      for (const event of events) {
        await remove(
          ref(firebaseDb, `events/${event}/${firebaseEventsInstance.encodeEmail(user.email)}`)
        );
      }

      setIsLoading(true);
      startCampaign(data);
    } catch (error) {
      console.error("Error starting outreach:", error);
      setError(error.message || "Failed to start outreach");
      setIsLoading(false);
      addNotification({
        type: "error",
        message: error.message || "Failed to start outreach",
      });
    }
  };

  const startCampaign = async (data) => {
    console.log("data:", data);
    try {
      // Get user email from user object
      const userEmail = user?.email;
      if (!userEmail) {
        throw new Error("User email not found");
      }

      // Add user_email to campaign data
      const enrichedData = {
        ...data,
        user_email: userEmail,
      };

      if (!isConnected) {
        throw new Error("No connection available. Please try again.");
      }

      setError(null);
      setIsLoading(true);

      return new Promise((resolve, reject) => {
        let campaignTimeout;
        let initTimeout;
        let hasStartedProcessing = false;
        let lastProgressTime = Date.now();

        const cleanup = () => {
          console.log("Cleaning up event handlers...");
          if (initTimeout) clearInterval(initTimeout);
          if (campaignTimeout) clearTimeout(campaignTimeout);
          off("agents_initialized");
          off("campaign_started");
          off("initialization_error");
          off("campaign_error");
          off("start_campaign_error");
          console.log("Dashboard event listeners cleaned up");
        };

        const handleInitError = (error) => {
          console.error("Initialization error occurred:", error);
          cleanup();
          const errorMessage = error.message?.includes("NoneType")
            ? "Failed to initialize agent communication. Please try again."
            : error.message || "Failed to initialize agents";

          setError(errorMessage);
          setIsLoading(false);
          setCampaignState({
            // Don't change isInitializing here - only campaign_started should hide it
            isStarting: false,
            error: errorMessage,
          });
          addNotification({
            type: "error",
            message: errorMessage,
          });
          reject(new Error(errorMessage));
        };

        // Initialize new campaign
        console.log("Starting campaign with data:", enrichedData);
        setCampaignState({
          isInitializing: true,  // Always show initializing UI when starting a campaign ; previously agentsRef.current.length===0 worked too (revert to this if you have issue with starting campaign)
          isStarting: false,
          error: null,
        });
        // Handle initialization progress
        const handleProgress = (data) => {
          console.log("Initialization progress:", data);
          hasStartedProcessing = true;
          lastProgressTime = Date.now();
        };

        // Set up only the listeners that aren't already registered
        on("start_campaign_error", (error) => {
          console.error("Campaign start error:", error);
          handleInitError(
            new Error(error.message || "Failed to start campaign")
          );
        });

        // Note: agents_initialized is already handled by the combined handler in useEffect
        // We'll just handle the normal flow logic here when needed
        const processNormalStartAgentsInitialized = async (campaignStartedResponse) => {
          console.log("[START] Received agents_initialized:", campaignStartedResponse);
          console.log("[START] Response structure:", {
            isObject: typeof campaignStartedResponse === "object",
            hasAgents: Boolean(campaignStartedResponse?.agents),
            agentsCount: campaignStartedResponse?.agents?.length || 0,
            campaignId: campaignStartedResponse?.campaign_id,
            hasSuccess: Boolean(campaignStartedResponse?.success),
            isResuming: false,
          });

          // Check if this is an invalid event or if we got string data
          if (
            !campaignStartedResponse ||
            typeof campaignStartedResponse === "string" ||
            typeof campaignStartedResponse !== "object"
          ) {
            console.error(
              "Invalid agents_initialized event data:",
              campaignStartedResponse
            );
            handleInitError(new Error("Invalid agents_initialized event data"));
            return;
          }

          console.log(
            "[START] Processing agents_initialized event:",
            campaignStartedResponse
          );

          if (
            campaignStartedResponse.success ||
            campaignStartedResponse.message ===
              "Sales Agent Initialized successfully" ||
            campaignStartedResponse.message === "Resuming campaign..."
          ) {
            try {
              console.log(
                "[RESUME] Agents initialized - checking if resuming or new campaign"
              );

              // Check if this is a resume operation
              const isResuming = campaignStartedResponse.content?.startsWith("Resuming campaign") || 
                               campaignStartedResponse.message === "Resuming campaign...";
              
              console.log("[RESUME] Is resuming?", isResuming, {
                content: campaignStartedResponse.content,
                message: campaignStartedResponse.message
              });

              if (isResuming) {
                // Only show InitializingUI if no agents yet
                console.log("[RESUME] Resume flow detected, agents count:", agents.length);
                setCampaignState((prev) => ({
                  ...prev,
                  isInitializing: agentsRef.current.length === 0,  // Only show if no agents
                  isStarting: false,
                  campaign_id: campaignStartedResponse.campaign_id,
                  scheduler_link: campaignStartedResponse.scheduler_link || prev.scheduler_link,
                  website: campaignStartedResponse.website || prev.website,
                  mode: campaignStartedResponse.mode || prev.mode,
                  error: null,
                  status: "resuming", // Add a resuming status
                }));
              } else {
                // For normal campaign starts, also store the scheduler_link
                setCampaignState((prev) => ({
                  ...prev,
                  campaign_id: campaignStartedResponse.campaign_id,
                  scheduler_link: campaignStartedResponse.scheduler_link || prev.scheduler_link,
                  website: campaignStartedResponse.website || prev.website,
                  mode: campaignStartedResponse.mode || prev.mode,
                }));
              }
              
              // Store scheduler_link for prefilling future campaigns
              if (campaignStartedResponse.scheduler_link) {
                setSchedulerLink(campaignStartedResponse.scheduler_link);
                console.log("[CAMPAIGN] Storing scheduler_link for prefilling:", campaignStartedResponse.scheduler_link);
              }

              // Clear initialization timeout since we've successfully initialized agents
              if (initTimeout) {
                clearInterval(initTimeout);
                initTimeout = null;
              }

              // Get the latest event data from Firebase
              const latestEvent = Object.entries(campaignStartedResponse)
                .filter(
                  ([key, value]) =>
                    key.startsWith("-") && value && typeof value === "object"
                )
                .sort((a, b) => b[0].localeCompare(a[0]))
                .map((entry) => entry[1])[0]; // Use map like campaign_started handler

              if (latestEvent && latestEvent.agents) {
                // Access agents directly from mapped event
                console.log(
                  "Setting agents from Firebase:",
                  latestEvent.agents
                );
                setAgents(latestEvent.agents);

                // Only set to active if not resuming (for resume, wait for campaign_started)
                if (!isResuming) {
                  setCampaignState((prev) => ({
                    ...prev,
                    campaign_id:
                      latestEvent.campaign_id ||
                      campaignStartedResponse.campaign_id,
                    status: "active",
                    error: null,
                  }));
                }
              }

              // Only emit start_campaign if we don't have a recent successful campaign_started event
              const campaignStartedEvents = Object.entries(
                campaignStartedResponse || {}
              )
                .filter(
                  ([key, value]) =>
                    key.startsWith("-") && value && typeof value === "object"
                )
                .sort((a, b) => b[0].localeCompare(a[0])); // Sort by Firebase push ID (chronological)

              const latestCampaignStarted =
                campaignStartedEvents.length > 0
                  ? campaignStartedEvents[0][1]
                  : null;

              // Check if we have a recent successful campaign with matching ID
              const isRecentAndValid =
                latestCampaignStarted &&
                latestCampaignStarted.success &&
                latestCampaignStarted.campaign_id ===
                  campaignStartedResponse.campaign_id &&
                latestCampaignStarted.timestamp > Date.now() - 5 * 60 * 1000; // Within last 5 minutes

              if (!campaignActive && !isRecentAndValid) {
                setShowOutreachDialog(false);

                // Add a small delay to ensure Firebase has time to write the event
                await new Promise((resolve) => setTimeout(resolve, 1000));

                // Now that agents are initialized, start the campaign
                const campaignPayload = {
                  ...enrichedData, // Use enrichedData which already has user_email
                  campaign_id: campaignStartedResponse.campaign_id,
                };

                console.log(
                  "Emitting start_campaign with payload:",
                  campaignPayload
                );
                await emit("start_campaign", campaignPayload);
                console.log(
                  "Start campaign event emitted, waiting for campaign_started event..."
                );
              } else {
                console.log("Skipping start_campaign emission:", {
                  campaignActive,
                  latestCampaignStarted: latestCampaignStarted
                    ? {
                        success: latestCampaignStarted.success,
                        campaign_id: latestCampaignStarted.campaign_id,
                        current_campaign_id:
                          campaignStartedResponse.campaign_id,
                        timestamp: latestCampaignStarted.timestamp,
                        age_minutes:
                          (Date.now() - latestCampaignStarted.timestamp) /
                          (60 * 1000),
                      }
                    : null,
                });
                setShowOutreachDialog(false);
              }
            } catch (error) {
              console.error("Failed to emit start_campaign:", error);
              handleInitError(error);
            }
          } else {
            handleInitError(
              new Error(
                campaignStartedResponse?.message ||
                  "Agent initialization failed"
              )
            );
          }
        };
        
        // Store function for access by the combined handler
        window.processNormalStartAgentsInitialized = processNormalStartAgentsInitialized;
        
        // Note: campaign_started is already handled by the handlers object
        // No need to register it again here

        // Set up timeout checking
        initTimeout = setInterval(() => {
          const timeSinceLastProgress = Date.now() - lastProgressTime;

          // Check if we have agents - if so, we can consider initialization successful
          if (agents && agents.length > 0) {
            console.log("Agents detected, clearing initialization timeout");
            clearInterval(initTimeout);
            off("initialization_progress", handleProgress);
            return;
          }

          // Only timeout if we've started processing and then stopped
          // Increased timeout from 90000 to 180000 (3 minutes) to allow more time for backend processing
          if (hasStartedProcessing && timeSinceLastProgress > 180000) {
            clearInterval(initTimeout);
            off("initialization_progress", handleProgress);
            handleInitError(
              new Error("Initialization stalled. Please try again.")
            );
          }
          // Initial startup timeout - longer to account for cold starts
          // Increased timeout from 120000 to 240000 (4 minutes) to allow more time for backend startup
          else if (!hasStartedProcessing && timeSinceLastProgress > 240000) {
            clearInterval(initTimeout);
            off("initialization_progress", handleProgress);
            handleInitError(
              new Error("Initialization timed out. Please try again.")
            );
          }
        }, 5000);

        // First initialize the agents
        const initPayload = {
          ...enrichedData,
        };
        console.log("Emitting initialize_agents with:", initPayload);
        emit("initialize_agents", initPayload);
        console.log("Initialize agents event emitted");

        // Handle connection errors
        on("connect_error", (error) => {
          console.error("Connection error during initialization:", error);
          handleInitError(new Error(`Connection error: ${error.message}`));
        });

        on("error", (error) => {
          console.error("Socket error during initialization:", error);
          handleInitError(
            new Error(`Socket error: ${error.message || "Unknown error"}`)
          );
        });
      });
    } catch (error) {
      console.error("Error in startCampaign:", error);
      setIsLoading(false);
      setError(error.message || "Failed to start campaign");
      addNotification({
        type: "error",
        message: error.message || "Failed to start campaign"
      });
    }
  };

  const handleCampaignStateChange = useCallback(
    (active, newMetrics = {}) => {
      if (!newMetrics.completed) setCampaignActive(active);
      
      // Handle feedback mode updates from AgentChat
      if (newMetrics.feedbackMode !== undefined) {
        _setFeedbackMode(newMetrics.feedbackMode);
      }
      if (newMetrics.currentEmail !== undefined) {
        _setCurrentEmail((prev) => {
          const nextId = newMetrics.currentEmail ?? null;
          const prevId = prev?.id ?? null;
          if (prevId === nextId) return prev;
          return nextId ? { id: nextId } : null;
        });
      }
      
      setCampaignState((prev) => ({
        ...prev,
        // Don't change isInitializing here - only campaign_started should hide it
        isStarting: false,
        metrics: { ...prev.metrics, ...newMetrics },
      }));
      if (newMetrics.completed) {
        setShowCompletionDialog(true);
      }

    },
    [_setFeedbackMode, _setCurrentEmail]
  );

  // Detect campaign completion - removed automatic dialog on inactive
  // Completion should only be triggered by explicit backend signal
  const prevActiveRef = useRef(campaignActive);
  useEffect(() => {
    // Only update the ref, don't auto-show dialog
    prevActiveRef.current = campaignActive;
  }, [campaignActive]);

  // performance card toggle on mobile screens
  const { isOpen } = usePerformanceCard();
  const isMobile = useIsMobile();

  const showClass = isMobile && isOpen ? "open" : "";
  const mainContentClass = `main-content ${isMobile && !isOpen ? "shifted" : ""}`;
  const dashboardClass = `dashboard ${showClass}`;

  // Define isStartButtonDisabled
  const isStartButtonDisabled = !isConnected || 
    campaignState.isInitializing || 
    campaignState.isStarting;

  return (
    <motion.div className={dashboardClass}>
      <div className="dashboard-header">
        <div
          className={`connection-status ${
            connectionState === CONNECTION_STATES.CONNECTED
              ? "connected"
              : "disconnected"
          }`}
        >
          {/* Full status text visible only on desktop screens */}
          <span>
            {connectionState === CONNECTION_STATES.CONNECTED
              ? `Connected${agents?.length ? ` (${agents.length} active)` : ""}`
              : "Connecting..."}
          </span>

          {/* Mobile-only count next to the colored circle */}
          {connectionState === CONNECTION_STATES.CONNECTED && isMobile &&
            agents?.length > 0 && (
              <span className="mobile-count">{agents.length}</span>
            )}
        </div>

        <div className="header-actions">
          {campaignActive ? (
            <button
              className={`mode-toggle-button ${campaignMode === 'autonomous' ? 'pressed' : ''}`}
              onClick={handleToggleMode}
              disabled={isTogglingMode}
              title={`Switch to ${campaignMode === 'autonomous' ? 'Supervised' : 'Autonomous'} Mode`}
            >
              <span className="mode-icon">{campaignMode === 'autonomous' ? '🤖' : '👤'}</span>
              <span className="mode-text">{campaignMode === 'autonomous' ? 'Autonomous' : 'Supervised'}</span>
            </button>
          ) : (
            <button
              className={`start-outreach-button ${isLoading ? "loading" : ""}`}
              onClick={() => setShowOutreachDialog(true)}
              disabled={isStartButtonDisabled}
            >
              {isLoading
                ? "Initializing..."
                : campaignState.isStarting
                  ? "Starting Campaign..."
                  : "New Campaign"}
            </button>
          )}
          <ProfileMenu user={user} onLogout={onLogout} campaignActive={campaignActive} />
        </div>
      </div>

      {/* Debug InitializingUI condition */}
      {console.log("[INIT_UI_DEBUG] Showing InitializingUI?", {
        "campaignState.isInitializing": campaignState.isInitializing,
        "resumeState?.isResuming": resumeState?.isResuming,
        "agents.length": agents.length,
        "campaignActive": campaignActive,
        "campaignState.status": campaignState.status,
      })}
      {console.log("[INIT_UI_DEBUG] Showing InitializingUI Condition", { 
        "condition1": (campaignState.isInitializing && campaignState.status !== 'stopped' && campaignState.status !== 'active'),
        "condition2": (resumeState?.isResuming === true && agents.length === 0),
        "condition3": (campaignActive && agents.length === 0 && campaignState.status !== 'completed' && campaignState.status !== 'active'),
        "overall": ((campaignState.isInitializing && campaignState.status !== 'stopped' && campaignState.status !== 'active') || (resumeState?.isResuming === true && agents.length === 0) || (campaignActive && agents.length === 0 && campaignState.status !== 'completed' && campaignState.status !== 'active'))
      })}
      
      {((campaignState.isInitializing && campaignState.status !== 'stopped' && campaignState.status !== 'active') || (resumeState?.isResuming === true && agents.length === 0) || (campaignActive && agents.length === 0 && campaignState.status !== 'completed' && campaignState.status !== 'active')) ? (
        <div className="main-content-2">
          <InitializingUI />
        </div>
      ) : !campaignActive ? (
        <div className="main-content-2">
          <PlaceholderUI
            campaignId={campaignId}
            initialUrl={externalCampaignState?.url || ""}
            onSend={(url, shouldSubmit = true, data = null) => {
              // Handle list campaign with enriched prospects
              if (url === 'list_campaign' && data) {
                setCampaignUrl(data.websiteUrl || null);  // Use website URL from CSV upload
                setEnrichedProspects(data.prospects);
                setJobToBeDone(data.job_to_be_done);
                setShowOutreachDialog(true);
                return;
              }
              
              // Always update the campaign state with the URL
              if (onCampaignStateChange) {
                onCampaignStateChange({ url });
              }
              
              // Only show dialog if it's a submission (not just saving)
              if (shouldSubmit) {
                setCampaignUrl(url);
                setShowOutreachDialog(true);
              }
            }}
          />
        </div>
      ) : (
        <>
          <div className={`metrics-section ${showClass}`}>
            {console.log("[Dashboard] Passing metrics to PerformanceDashboard:", campaignState.metrics)}
            <PerformanceDashboard
              metrics={campaignState.metrics}
              agentCount={agents.length}
              isActive={campaignActive}
              startTime={campaignState.startTime}
            />
          </div>
          <div className={`dashboard-content-container ${showActivityPanel ? 'panel-visible' : ''}`}>
            {showActivityPanel && (
              <ActivityPanel
                agents={agents}
                currentProspect={currentProspectInfo}
                isExpanded={isPanelExpanded}
                onExpand={handleExpandPanel}
                onClose={handleToggleActivityPanel}
                canvasMode={canvasMode}
                canvasFilter={canvasFilter}
              />
            )}
            {showVisionDisplay && (
              <div className="vision-display-section">
                <VisionDisplay
                  activity={selectedActivity}
                  fallbackProspect={currentProspectInfo}
                  isOpen={showVisionDisplay}
                  onClose={() => {
                    setShowVisionDisplay(false);
                    setSelectedActivity(null);
                  }}
                />
              </div>
            )}
            <div className={`${mainContentClass}`}>
              {/* Enhanced Agent Chat with COO Integration */}
              <AgentChat
                ref={agentChatRef}
                agents={agents}
                campaignActive={campaignActive}
                campaignState={campaignState}
                onCampaignStateChange={handleCampaignStateChange}
                dashboardConnectionState={connectionState}
                isConnected={isConnected}
                campaignMode={campaignMode}
                onToggleActivityPanel={handleToggleActivityPanel}
                showActivityPanel={showActivityPanel}
                hideInput={!needsHumanApproval()}
                animateFromButton={false}  // Always false since AgentChat is always visible
                forceExpand={campaignActive && campaignState?.status === 'active' && agents && agents.length > 0 ? needsHumanApproval() : false}
                // COO Integration Props
                showCOOButton={showCOOButton}
                expandMode={campaignMode === 'supervised' && !!_currentEmail}
                onCOOMessage={handleCOOMessage}
                onCOOFileUpload={handleCOOFileUpload}
                onCOOAssistantMessage={(content) => {
                  console.log('Assistant message received in Dashboard:', content);
                  // Handle assistant messages if needed
                }}
              />
            </div>
          </div>
        </>
      )}

      <AnimatePresence mode="sync">
        {error && (
          <motion.div
            key="error-message"
            className="error-message"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
          >
            {error}
            <button onClick={() => setError(null)}>Dismiss</button>
          </motion.div>
        )}

        {showOutreachDialog && (
          <motion.div
            key="outreach-dialog"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
          >
            <OutreachDialog
              open={showOutreachDialog}
              onStart={handleStartOutreach}
              onClose={() => {
                setShowOutreachDialog(false);
                setEnrichedProspects(null);
                setJobToBeDone(null);
                setIsLoading(false);
                setCampaignState({
                  // Don't change isInitializing here - only campaign_started should hide it
                  isStarting: false,
                  error: null,
                });
              }}
              loading={isLoading}
              url={campaignUrl}
              enrichedProspects={enrichedProspects}
              jobToBeDone={jobToBeDone}
              calendlyLink={schedulerLink}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {showCompletionDialog && (
        <CampaignCompleteDialog
          open={showCompletionDialog}
          onRunAgain={() => {
            setShowCompletionDialog(false);
            // re-open outreach dialog with previous settings
            setShowOutreachDialog(true);
          }}
          onNewCampaign={() => {
            setShowCompletionDialog(false);
            // reset and open outreach dialog fresh
            setShowOutreachDialog(true);
          }}
          onClose={() => setShowCompletionDialog(false)}
        />
      )}

             
    </motion.div>
  );
}
export default Dashboard;
